import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../common/state/store";

export interface IFlowViewport {
  projectId: number;
  x: number;
  y: number;
  zoom: number;
}

export interface IFlowChainsState {
  projectId: number;
  collapsedKeys: string[];
  foldAllMode: boolean;
}

interface IGraphViewState {
  projectId: number | null;
  scale: number;
  translateX: number;
  translateY: number;
  flowViewport: IFlowViewport | null;
  flowChains: IFlowChainsState | null;
  interiorSceneStack: number[];
}

const initialState: IGraphViewState = {
  projectId: null,
  scale: 1,
  translateX: 0,
  translateY: 0,
  flowViewport: null,
  flowChains: null,
  interiorSceneStack: [],
};

export const graphViewSlice = createSlice({
  name: "graphView",
  initialState,
  reducers: {
    setGraphView: (
      state,
      action: PayloadAction<
        Omit<IGraphViewState, "flowViewport" | "flowChains" | "interiorSceneStack">
      >,
    ) => {
      state.projectId = action.payload.projectId;
      state.scale = action.payload.scale;
      state.translateX = action.payload.translateX;
      state.translateY = action.payload.translateY;
    },
    setFlowGraphView: (state, action: PayloadAction<IFlowViewport>) => {
      state.flowViewport = action.payload;
    },
    setFlowChains: (state, action: PayloadAction<IFlowChainsState>) => {
      state.flowChains = action.payload;
    },
    setInteriorSceneStack: (state, action: PayloadAction<number[]>) => {
      state.interiorSceneStack = action.payload;
    },
    resetGraphView: () => initialState,
  },
});

export const {
  setGraphView,
  setFlowGraphView,
  setFlowChains,
  setInteriorSceneStack,
  resetGraphView,
} = graphViewSlice.actions;

export const getGraphView = (state: RootState) => state.graphView;
export const getFlowGraphView = (state: RootState) => state.graphView.flowViewport;
export const getFlowChains = (state: RootState) => state.graphView.flowChains;
export const getInteriorSceneStack = (state: RootState) => state.graphView.interiorSceneStack;

export default graphViewSlice.reducer;
